/**
 * uid — the Swiss company UID check (api + web).
 *
 * A UID is CHE-xxx.xxx.xxx; its 9th digit is the mod-11 check digit of the first 8, with weights
 * 5,4,3,2,7,6,5,4: check = 11 − (sum mod 11), 11 → 0, and 10 is never issued. A UID failing the
 * check is a misread or a typo, never a real company.
 */

/** The canonical written form, CHE-xxx.xxx.xxx. */
export const CANONICAL_UID_RE = /^CHE-\d{3}\.\d{3}\.\d{3}$/;

const UID_CHECK_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4] as const;

/** True when the 9 digits of a UID (separators and prefix ignored) end in a valid check digit. */
export function hasValidUidCheckDigit(uid: string): boolean {
  const d = uid.replace(/\D/g, '').split('').map(Number);
  if (d.length !== 9) return false;
  const sum = UID_CHECK_WEIGHTS.reduce((acc, w, i) => acc + w * d[i]!, 0);
  const check = (11 - (sum % 11)) % 11;
  return check !== 10 && check === d[8];
}

/** True for a canonical CHE-xxx.xxx.xxx UID with a valid check digit. */
export function isValidUid(uid: string): boolean {
  return CANONICAL_UID_RE.test(uid) && hasValidUidCheckDigit(uid);
}
